import { useEffect, useId, useRef, useState, ReactNode } from 'react';

/**
 * Tarjeta de gráfica con botón "Ampliar" → abre la gráfica a pantalla grande.
 * `render(height)` se usa dos veces (chica en la tarjeta, grande al ampliar).
 *
 * 0.46.0 — que cada gráfica se explique sola: el título es la PREGUNTA que contesta ("¿Qué canal trae gente que sí
 * llega?"), `como` dice en una línea cómo leerla (y que se puede tocar), y sin datos dice POR QUÉ (`vacio`), no
 * "sin datos" a secas. Ampliada es una ventana de la app: Escape, el fondo o "Cerrar" la cierran (si hay una ventana
 * de detalle encima, Escape cierra primero ésa).
 */
export function ChartCard({ title, hint, como, render, data, foot, vacio, datos }: {
  title: string; hint?: string; como?: ReactNode; render: (h: number) => ReactNode; data?: unknown[]; foot?: ReactNode; vacio?: ReactNode; datos?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  // Sin datos NO se pinta la gráfica. Recharts, con un arreglo vacío, dibuja unos ejes con escala 0–1: parece una
  // gráfica de verdad reportando casi cero. Se dice que no hay datos y, si se sabe, por qué.
  const sinDatos = Array.isArray(data) && data.length === 0;
  const Vacio = ({ h }: { h: number }) => (
    <div className="muted" data-vacio style={{ minHeight: Math.min(h, 160), display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 13, padding: 12, lineHeight: 1.5 }}>
      {vacio || 'Sin datos para este periodo.'}
    </div>
  );
  const bigH = Math.min(560, typeof window !== 'undefined' ? window.innerHeight * 0.68 : 480);
  useEffect(() => {
    if (!open) return;
    const antes = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const tecla = (e: KeyboardEvent) => {
      // Una ventana de detalle o un diálogo encima se cierran primero (cada uno escucha su Escape).
      if (e.key !== 'Escape' || document.querySelector('[data-ventana], [data-dialogo]')) return;
      setOpen(false);
    };
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('keydown', tecla); antes?.focus?.(); };
  }, [open]);
  return (
    <>
      {/* overflow-x: clip — el globo de una gráfica (tooltip) se anima desde donde estaba y por un instante se asomaba fuera de
          la tarjeta: en celular eso es scroll de lado. */}
      <div className="card" style={{ padding: '14px 16px', minWidth: 0, overflowX: 'clip' }} data-grafica={datos || ''}>
        {/* Con una pista al lado, en celular el título se apretaba a 4 renglones: si no caben los dos, la pista y
            "Ampliar" bajan a su renglón y el título se queda con el ancho. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: como ? 2 : 10, columnGap: 8, rowGap: 2 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--gold-dark)', minWidth: 0, flex: '1 1 12em' }}>{title}</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexShrink: 0, marginLeft: 'auto' }}>
            {hint && <span className="muted" style={{ fontSize: 11 }}>{hint}</span>}
            {!sinDatos && <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setOpen(true)} aria-label={`Ampliar: ${title}`}>Ampliar</button>}
          </div>
        </div>
        {como && <div className="muted" data-como style={{ fontSize: 12, lineHeight: 1.45, marginBottom: 10 }}>{como}</div>}
        {sinDatos ? <Vacio h={240} /> : render(240)}
        {foot && <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.55, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>{foot}</div>}
      </div>
      {open && (
        <div onClick={() => setOpen(false)} data-ampliada-fondo style={{ position: 'fixed', inset: 0, background: 'rgba(40,33,40,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={id} data-ampliada onClick={(e) => e.stopPropagation()} className="card"
            style={{ width: 'min(1040px, 96vw)', maxHeight: '92vh', padding: 20, overflow: 'auto', outline: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
              <h3 id={id} style={{ margin: 0, fontSize: 18, color: 'var(--gold-dark)' }}>{title}</h3>
              <button className="btn-ghost" onClick={() => setOpen(false)}>Cerrar ✕</button>
            </div>
            {como && <div className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>{como}</div>}
            {sinDatos ? <Vacio h={bigH} /> : render(bigH)}
            {foot && <div className="muted" style={{ fontSize: 13, lineHeight: 1.6, marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>{foot}</div>}
          </div>
        </div>
      )}
    </>
  );
}
